import { useEffect, useRef, useState } from "preact/hooks";
import { track } from "./lib/analytics";
import { initialState, type FormState } from "./state";
import { Step1Contact } from "./steps/Step1Contact";
import { Step2Loads } from "./steps/Step2Loads";
import { Step3Bill } from "./steps/Step3Bill";
import { Step4Recommendation } from "./steps/Step4Recommendation";
import { Step5Confirmation } from "./steps/Step5Confirmation";

export interface StepProps {
  state: FormState;
  update: (patch: Partial<FormState>) => void;
  next: () => void;
  back: () => void;
  /** Human-readable label of the current step, for the in-step contact affordance. */
  stepName: string;
}

const STEP_LABELS = ["Your home", "Contact", "Utility bill", "Recommendation", "Done"];

export function App() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<FormState>(initialState);
  const mainRef = useRef<HTMLElement>(null);
  const mounted = useRef(false);

  // Embedded on the marketing site (loader.js appends ?embed=1): render just the
  // form — the host page already provides the KS header/footer chrome.
  const embed =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("embed");

  const update = (patch: Partial<FormState>) => setState((s) => ({ ...s, ...patch }));
  const next = () => setStep((n) => Math.min(n + 1, 4));
  const back = () => setStep((n) => Math.max(n - 1, 0));

  // Funnel step views: one event per step reached, carrying the submission id so
  // the whole funnel stitches together in GA4. Runs on mount (step 0) too.
  useEffect(() => {
    track("funnel_step_view", {
      step_index: step + 1,
      step_name: STEP_LABELS[step],
      submission_id: state.submissionId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Focus management: on each step change (not the initial mount) move focus to
  // the new step's heading, so keyboard users don't lose their place and screen
  // readers announce the step they landed on. preventScroll keeps the embedding
  // host page from jumping. tabindex=-1 makes the heading a scripted focus target
  // without adding it to the tab order.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const heading = mainRef.current?.querySelector<HTMLElement>("h2");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    }
  }, [step]);

  // In embed mode, drop the page background so the host page shows through the iframe.
  useEffect(() => {
    if (embed) document.body.classList.add("embed-body");
    return () => document.body.classList.remove("embed-body");
  }, [embed]);

  // Auto-resize the embedding iframe (loader.js listens for this).
  useEffect(() => {
    const post = () =>
      window.parent?.postMessage(
        { type: "ks-generator:resize", height: document.body.scrollHeight },
        "*",
      );
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [step]);

  const props: StepProps = { state, update, next, back, stepName: STEP_LABELS[step] };

  return (
    <div class={embed ? "page embed" : "page"}>
      {!embed && (
        <header class="site-header">
          <a href="https://kasselmansolar.com" target="_blank" rel="noopener noreferrer">
            <img class="site-logo" src="/kasselman-full-logo.png" alt="Kasselman Solar" />
          </a>
          <p class="site-tagline">Reputation forged by results, relationships built on trust.</p>
        </header>
      )}

      <main class="wizard" ref={mainRef}>
        <ol class="progress" aria-label="Progress">
          {STEP_LABELS.map((label, i) => (
            <li
              key={label}
              class={i === step ? "current" : i < step ? "done" : ""}
              aria-current={i === step ? "step" : undefined}
            >
              <span class="dot" aria-hidden="true">{i + 1}</span>
              <span class="label">{label}</span>
            </li>
          ))}
        </ol>

        {step === 0 && <Step2Loads {...props} />}
        {step === 1 && <Step1Contact {...props} />}
        {step === 2 && <Step3Bill {...props} />}
        {step === 3 && <Step4Recommendation {...props} />}
        {step === 4 && <Step5Confirmation {...props} />}
      </main>

      {!embed && (
        <footer class="site-footer">
          <img class="footer-logo" src="/kasselman-full-logo.png" alt="Kasselman Solar" />
          <p class="footer-contact">
            <a href="tel:+15184788365">Upstate (518) 478-8365</a>
            <span class="sep" aria-hidden="true">·</span>
            <a href="tel:+18456814088">Hudson Valley (845) 681-4088</a>
            <span class="sep" aria-hidden="true">·</span>
            <a href="mailto:info@kasselmansolar.com">info@kasselmansolar.com</a>
          </p>
          <p class="footer-creds">
            4,000+ solar installations across New York · NYSERDA Platinum Quality Solar Installer
          </p>
        </footer>
      )}
    </div>
  );
}
